/**
 * The minimal column shape this util reads/writes. Every list table's
 * `ColumnDef` (Service / Movement / Structure / User) structurally satisfies
 * this, so the helpers below stay generic over the caller's concrete type
 * instead of coupling to one table's `ColumnDef`.
 */
export interface ColumnLike {
  key: string;
  visible?: boolean;
  width?: number;
}

/**
 * Per-list-page column layout persisted in the browser session only
 * (sessionStorage) — column order, which columns are visible, and any
 * user-dragged widths. Survives reloads / back-navigation within the session,
 * is NOT synced to the server and does NOT persist across sessions. Saved
 * reports take precedence; this is the fallback for the default (no-report) view.
 */
export interface SessionColumnState {
  /** All column keys in their current display order. */
  order: string[];
  /** The subset of keys that are currently visible. */
  visible: string[];
  /** User-dragged column widths in px, keyed by column key. */
  widths: Record<string, number>;
}

const keyFor = (pageName: string) => `table-columns-${pageName}`;

/** Lower/upper bounds applied to a column's auto-measured content width when a
 *  table first freezes to fixed-pixel widths — keeps a tiny column usable and
 *  stops one runaway cell from producing an absurdly wide column. Users can
 *  still drag outside these afterwards. */
export const MIN_AUTO_COLUMN_WIDTH = 40;
export const MAX_AUTO_COLUMN_WIDTH = 600;

/** Clamp an auto-measured content width to the sane default range above. */
export const clampAutoColumnWidth = (width: number): number =>
  Math.min(MAX_AUTO_COLUMN_WIDTH, Math.max(MIN_AUTO_COLUMN_WIDTH, width));

/**
 * Apply a header resize: set the dragged column's width, and freeze any
 * not-yet-sized columns to their on-screen width from `seed` so only the dragged
 * column changes from then on. Shared by every list page's resize handler.
 */
export const applyColumnWidth = <T extends ColumnLike>(
  columns: T[],
  key: string,
  width: number,
  seed?: Record<string, number>
): T[] =>
  columns.map((c) => {
    if (c.key === key) return { ...c, width };
    if (seed && c.width == null && seed[c.key] != null) return { ...c, width: seed[c.key] };
    return c;
  });

/**
 * Clear every user-dragged width. Reports don't store widths, so a report
 * renders in its own (unfrozen) layout instead of inheriting the default view's
 * frozen widths.
 */
export const dropColumnWidths = <T extends ColumnLike>(columns: T[]): T[] =>
  columns.map((c) => (c.width === undefined ? c : { ...c, width: undefined }));

/** Read the saved session layout for a page, or null if none / unreadable. */
export const readSessionColumnState = (pageName: string): SessionColumnState | null => {
  try {
    const raw = sessionStorage.getItem(keyFor(pageName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionColumnState>;
    return {
      order: Array.isArray(parsed.order) ? parsed.order : [],
      visible: Array.isArray(parsed.visible) ? parsed.visible : [],
      widths: parsed.widths && typeof parsed.widths === 'object' ? parsed.widths : {},
    };
  } catch {
    return null;
  }
};

/** Persist the current column layout for a page (derives order/visible/widths). */
export const writeSessionColumnState = <T extends ColumnLike>(
  pageName: string,
  columns: T[]
): void => {
  try {
    const state: SessionColumnState = {
      order: columns.map((c) => c.key),
      visible: columns.filter((c) => c.visible).map((c) => c.key),
      widths: columns.reduce<Record<string, number>>((acc, c) => {
        if (typeof c.width === 'number') acc[c.key] = c.width;
        return acc;
      }, {}),
    };
    sessionStorage.setItem(keyFor(pageName), JSON.stringify(state));
  } catch {
    // sessionStorage unavailable (private mode / quota) — silently skip.
  }
};

/** Clear a page's saved session layout (e.g. an explicit "reset columns"). */
export const clearSessionColumnState = (pageName: string): void => {
  try {
    sessionStorage.removeItem(keyFor(pageName));
  } catch {
    // ignore
  }
};

/**
 * Layer a saved session state onto freshly-built default column defs:
 * reorder to the saved order, apply saved visibility, and apply saved widths.
 * Columns added since the state was saved (not in `saved.order`) keep their
 * default position (appended after known columns) and default visibility.
 * Saved keys that no longer exist are ignored.
 */
export const applySessionColumnState = <T extends ColumnLike>(
  defaults: T[],
  saved: SessionColumnState | null
): T[] => {
  if (!saved || saved.order.length === 0) return defaults;

  const orderIndex = new Map(saved.order.map((k, i) => [k, i]));
  const known = defaults.filter((c) => orderIndex.has(c.key));
  const unknown = defaults.filter((c) => !orderIndex.has(c.key));
  known.sort((a, b) => (orderIndex.get(a.key) ?? 0) - (orderIndex.get(b.key) ?? 0));

  return [...known, ...unknown].map((c) => {
    const isKnown = orderIndex.has(c.key);
    const width = saved.widths[c.key];
    // Only apply a genuinely usable width. sessionStorage is user-writable, so a
    // corrupt/tampered blob could carry a non-finite, negative, or zero width
    // that would collapse the column or NaN the whole table width; drop those.
    // A legitimate user-dragged width (any finite > 0) is preserved as-is — no
    // upper clamp, since the drag itself is the user's intent.
    const usableWidth = Number.isFinite(width) && width > 0 ? width : undefined;
    // Spreading a generic `T` and overriding a few keys can't be proven
    // assignable back to `T` by the compiler (it could be a wider subtype), so
    // this single, well-understood cast localises that limitation here rather
    // than pushing `as ColumnDef[]` onto every caller.
    return {
      ...c,
      visible: isKnown ? saved.visible.includes(c.key) : c.visible,
      ...(usableWidth !== undefined ? { width: usableWidth } : {}),
    } as T;
  });
};
